import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { MousePointerClick } from "lucide-react";
import { cn } from "../lib/utils";
import { getAgentAbbreviation } from "../lib/chatUtils";

interface Assistant {
  id: string;
  name: string;
  assigned_conversations?: any[];
}

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  allAssistants: Assistant[];
  sessionUserId?: string | null;
  selectedAssistantId: string | null;
  setSelectedAssistantId: (id: string | null) => void;
  reassignNote: string;
  setReassignNote: (s: string) => void;
  onReassign: (assistantId: string) => void;
  t: (k: string) => string;
}

const ReassignDialog: React.FC<Props> = ({
  open,
  setOpen,
  allAssistants,
  sessionUserId,
  selectedAssistantId,
  setSelectedAssistantId,
  reassignNote,
  setReassignNote,
  onReassign,
  t,
}) => {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white w-[583px] rounded-xl shadow-xl px-6 py-6">
        <DialogHeader>
          <DialogTitle className="text-lg font-normal text-[#13161A]">{t("agents")}</DialogTitle>
          <p className="flex text-sm text-[#6F7D95] font-normal mt-1 gap-1 items-center">
            <MousePointerClick className="w-4 h-4 text-[#105BDB]" /> {t("select_an_option")}
          </p>
        </DialogHeader>

        <div className="flex flex-col max-h-84 overflow-y-auto mt-2 gap-2 overflow-y-auto custom-scrollbar">
          {[...allAssistants]
            .sort((a, b) => {
              if (a.id === sessionUserId) return -1;
              if (b.id === sessionUserId) return 1;
              return 0;
            })
            .map((assistant: any) => {
              const isSelected = selectedAssistantId === assistant.id;
              const abbreviation = getAgentAbbreviation(assistant.name, false);
              
              return (
                <div
                  key={assistant.id}
                  className={cn(
                    "rounded-md px-4 py-3 border cursor-pointer transition-all text-sm",
                    isSelected
                      ? "border-[#3B82F6] bg-[#EEF4FF] text-[#13161A]"
                      : "border border-[#E2E4E9] bg-white text-[#13161A] hover:bg-gray-50"
                  )}
                  onClick={() => setSelectedAssistantId(assistant.id)}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="font-medium flex items-center gap-1 text-[#252A32]">
                      <span>{abbreviation}</span>
                      {assistant.id === sessionUserId && (
                        <span className="text-sm text-[#252A32] font-normal">({t("myself")})</span>
                      )}
                    </div>
                    <div className="text-sm text-[#6F7D95] font-normal">
                      {assistant.assigned_conversations?.length || 0} tickets
                    </div>
                  </div>

                  {isSelected && (
                    <textarea
                      className="w-full mt-3 border border-[#E2E4E9] rounded-lg p-3 resize-none h-[90px] bg-white text-sm font-normal placeholder:text-[#6F7D95] focus:outline-none focus:ring-1 focus:ring-[#0064E0]"
                      placeholder={t("additional_information_optional")}
                      value={reassignNote}
                      onChange={(e) => setReassignNote(e.target.value)}
                    />
                  )}
                </div>
              );
            })}
        </div>

        <DialogFooter className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setSelectedAssistantId(null);
              setOpen(false);
            }}
            className="rounded-lg border border-[#D1D5DB] text-black px-4 py-2 hover:bg-gray-100"
          >
            {t("cancel")}
          </Button>
          <Button
            className={cn(
              "rounded-lg px-4 py-2 text-base font-medium",
              !selectedAssistantId
                ? "bg-[#EDEFF2] text-[#D5D9E0] cursor-not-allowed"
                : "bg-[#105BDB] text-white hover:bg-[#0055C4] active:bg-[#0049AE]"
            )}
            onClick={() => {
              if (selectedAssistantId) {
                onReassign(selectedAssistantId);
                setOpen(false);
              }
            }}
            disabled={!selectedAssistantId}
          >
            {t("reasign_to_user")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReassignDialog;
